import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';

import { ShopifySubscriptionDiscountOptions } from '@/components/forms/shopify-subscription-discount/ShopifySubscriptionDiscountForm';
import ConfirmDeleteModalString from '@/components/modal/common/ConfirmDeleteModalString';
import ViewUpdateCreateShopifySubscriptionDiscountModal from '@/components/modal/shopify-subscription-discount/ViewUpdateCreateShopifySubscriptionDiscountModal';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import ShopifySubscriptionDiscountTable from '@/components/table/shopify-subscription-discount/ShopifySubscriptionDiscountTable';
import Button from '@/components/ui/button';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { useModal } from '@/hooks/useModal';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IShopifySubscriptionDiscount } from '@/interfaces/api/shopify-subscription-discount/IShopifySubscriptionDiscount';
import { IShopifySubscriptionDiscountWithAction } from '@/interfaces/api/shopify-subscription-discount/IShopifySubscriptionDiscountWithAction';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const ShopifySubscriptionDiscounts = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [
		shopifySubscriptionDiscountOption,
		setShopifySubscriptionDiscountOption,
	] = useState<ShopifySubscriptionDiscountOptions>(
		ShopifySubscriptionDiscountOptions.CREATE,
	);
	const [
		modalShopifySubscriptionDiscountId,
		setModalShopifySubscriptionDiscountId,
	] = useState<string | undefined>(undefined);
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const [shopifySubscriptionDiscountId, setShopifySubscriptionDiscountId] =
		useState<string | undefined>(undefined);

	const {
		data: shopifySubscriptionDiscounts,
		refetch,
		isLoading: isFetching,
	} = useQuery<IShopifySubscriptionDiscount[]>({
		queryKey: [ApiQueryKeys.getAllShopifySubscriptionDiscounts],
		queryFn: () => haggleApiService.getAllShopifySubscriptionDiscounts(),
		refetchOnWindowFocus: false,
	});

	const { modal, openModal, closeModal } = useModal({
		children: (
			<ViewUpdateCreateShopifySubscriptionDiscountModal
				id={modalShopifySubscriptionDiscountId}
				closeAfterSubmit={() => {
					closeModal();
					if (refetch) refetch();
				}}
				defaultClose={() => closeModal()}
				shopifySubscriptionDiscountOption={shopifySubscriptionDiscountOption}
			/>
		),
		modalBoxClassName: 'w-full max-w-3xl',
		closeOnOutClick: false,
	});

	const openUnifiedModal = (
		option: ShopifySubscriptionDiscountOptions,
		id: string | undefined = undefined,
	) => {
		setShopifySubscriptionDiscountOption(option);
		setModalShopifySubscriptionDiscountId(id);
		openModal();
	};

	const handlePaginationOptions = (): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: 1,
			itemCount: shopifySubscriptionDiscounts?.length || 0,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	const handleDeleteShopifySubscriptionDiscount = async (id: string) => {
		setIsDeleteLoading(true);
		try {
			await haggleApiService.deleteShopifySubscriptionDiscount(id);

			notificationService.success(
				'Shopify Subscription Discount deleted successfully!',
			);
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to delete Shopify Subscription Discount: ${error}`,
				);
			}
		} finally {
			setIsDeleteLoading(false);
			refetch();
		}
	};

	const columnHelper =
		createColumnHelper<IShopifySubscriptionDiscountWithAction>();

	const columns = [
		columnHelper.accessor('id', {
			cell: (info) => (
				<div className="w-[50px] text-center">{info.getValue()}</div>
			),
			header: () => <div className="w-[50px] text-center">ID</div>,
		}),
		columnHelper.accessor('email', {
			cell: (info) => (
				<div className="w-[250px] text-center truncate" title={info.getValue()}>
					{info.getValue()}
				</div>
			),
			header: () => <div className="w-[250px] text-center">Email</div>,
		}),
		columnHelper.accessor('planName', {
			cell: (info) => (
				<div className="w-[120px] text-center">{info.getValue()}</div>
			),
			header: () => <div className="w-[120px] text-center">Plan</div>,
		}),
		columnHelper.accessor('percentage', {
			cell: (info) => (
				<div className="w-[100px] text-center">{info.getValue() * 100}%</div>
			),
			header: () => <div className="w-[100px] text-center">Percentage</div>,
		}),
		columnHelper.accessor('durationMonths', {
			cell: (info) => (
				<div className="w-[120px] text-center">{info.getValue()} months</div>
			),
			header: () => <div className="w-[120px] text-center">Duration</div>,
		}),
		columnHelper.accessor('isUsed', {
			cell: (info) => (
				<div className="w-[80px] text-center">
					{info.renderValue() ? 'Yes' : 'No'}
				</div>
			),
			header: () => <div className="w-[80px] text-center">Redeemed?</div>,
		}),
		columnHelper.accessor('action', {
			header: () => <div className="w-[135px] text-center">Action</div>,
			cell: (info) => (
				<div className="flex gap-2 w-[135px] justify-center">
					<button
						className="font-normal text-blue-500 hover:underline"
						onClick={() => {
							openUnifiedModal(
								ShopifySubscriptionDiscountOptions.VIEW,
								info.row.original.id,
							);
						}}
						data-test={`view-button-${info.row.original.id}`}
					>
						View
					</button>
					{!info.row.original.isUsed && (
						<>
							<button
								className="font-normal text-blue-500 hover:underline"
								onClick={() => {
									openUnifiedModal(
										ShopifySubscriptionDiscountOptions.UPDATE,
										info.row.original.id,
									);
								}}
								data-test={`update-button-${info.row.original.id}`}
							>
								Edit
							</button>

							<button
								className="font-normal text-red-500 hover:underline"
								onClick={() => handleOpenDeleteModal(info.row.original.id)}
								disabled={isDeleteLoading}
								data-test={`delete-button-${info.row.original.id}`}
							>
								Delete
							</button>
						</>
					)}
				</div>
			),
		}),
	];

	const handleOpenDeleteModal = (id: string) => {
		setShopifySubscriptionDiscountId(id);
		openConfirmDeleteModal();
	};

	const {
		modal: confirmDeleteModal,
		openModal: openConfirmDeleteModal,
		closeModal: closeConfirmDeleteModal,
	} = useModal({
		children: (
			<ConfirmDeleteModalString
				submit={handleDeleteShopifySubscriptionDiscount}
				closeModal={() => closeConfirmDeleteModal()}
				message={`Are you sure you want to delete the Shopify Subscription Discount with ID ${shopifySubscriptionDiscountId}?`}
				elementId={shopifySubscriptionDiscountId}
			/>
		),
		closeOnOutClick: false,
		modalBoxClassName: 'max-w-[600px]',
	});

	return (
		<div
			className="flex flex-col justify-center items-center w-full"
			data-test="shopify-subscription-discounts-dashboard"
		>
			<div className="w-full p-2">
				<Button
					transition="none"
					shape="rounded"
					className="py-2"
					onClick={() =>
						openUnifiedModal(ShopifySubscriptionDiscountOptions.CREATE)
					}
					data-test="create-shopify-subscription-discount-button"
				>
					Add a new Shopify Subscription Discount
				</Button>
			</div>
			{shopifySubscriptionDiscounts && !isFetching && (
				<ShopifySubscriptionDiscountTable
					collectionShopifySubscriptionDiscount={shopifySubscriptionDiscounts}
					paginationOption={handlePaginationOptions()}
					columns={columns}
				/>
			)}
			{modal}
			{confirmDeleteModal}
		</div>
	);
};

export default ShopifySubscriptionDiscounts;
